import { useMutation, useQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useAppView } from '@/hooks/useAppView'
import { getAdapter, hasAdapter } from '@/adapters/registry'
import type { FollowStatus } from '@/adapters/types'
import type { Platform } from '@/contexts/ProtocolContext/ProtocolContext'
import type { SocialGraphPage } from '@/services/socialGraphService'
import type { APSocialGraphPage } from '@/services/apSocialGraphService'
import type { ProfileData } from '@/pages/Profile/Profile.types'
import type { ProfileWithBridgeStatus } from '@/types/graphql'

/** Remove a profile by DID from all cached AT Protocol following list infinite queries */
function removeFromFollowingLists(
    queryClient: ReturnType<typeof useQueryClient>,
    targetDid: string
) {
    queryClient.setQueriesData<InfiniteData<SocialGraphPage>>({ queryKey: ['following'] }, old => {
        if (!old) return old
        return {
            ...old,
            pages: old.pages.map(page => ({
                ...page,
                profiles: page.profiles.filter(p => p.did !== targetDid),
            })),
        }
    })
}

/** Remove a profile by AP account ID from all cached AP following list infinite queries */
function removeFromAPFollowingLists(
    queryClient: ReturnType<typeof useQueryClient>,
    targetAccountId: string
) {
    queryClient.setQueriesData<InfiniteData<APSocialGraphPage>>(
        { queryKey: ['ap-following'] },
        old => {
            if (!old) return old
            return {
                ...old,
                pages: old.pages.map(page => ({
                    ...page,
                    profiles: page.profiles.filter(p => p.did !== targetAccountId),
                })),
            }
        }
    )
}

/**
 * Unified follow/unfollow hook that works across all protocols via the adapter layer.
 *
 * @param protocol - The protocol of the profile being followed
 * @param identifier - DID for Bluesky, handle for Mastodon/Pixelfed (e.g. user@mastodon.social)
 * @param options.initialFollowingUri - Pre-populated follow URI from the post's viewer state.
 *   Pass `string` if following (the follow record URI), `null` if known not following,
 *   or omit/`undefined` to fetch from the API. When provided, avoids an extra getProfile call.
 */
export function useFollowUser(
    platform: Platform,
    identifier: string | undefined,
    options?: { initialFollowingUri?: string | null }
) {
    const queryClient = useQueryClient()
    const { session, mastodonSession, pixelfedSession } = useAuth()
    const { sessionAgent } = useAppView()

    const adapter = hasAdapter(platform) ? getAdapter(platform) : null
    const adapterSession =
        platform === 'bluesky'
            ? session
            : platform === 'mastodon'
              ? mastodonSession
              : pixelfedSession

    const queryKey = ['socialGraph', platform, 'followStatus', identifier] as const

    // Captured once at mount — used as initialDataUpdatedAt so the outer staleTime (5 min) governs freshness
    const [mountedAt] = useState(Date.now)

    const hasInitialData = options?.initialFollowingUri !== undefined
    const { data: followStatus, isLoading: isStatusLoading } = useQuery({
        queryKey,
        queryFn: () => adapter!.getFollowStatus(adapterSession!, identifier!, { sessionAgent }),
        enabled: !!identifier && !!adapterSession && !!adapter,
        staleTime: 5 * 60 * 1000, // 5 minutes
        ...(hasInitialData && {
            initialData: {
                isFollowing: options!.initialFollowingUri !== null,
                followRef: options!.initialFollowingUri ?? undefined,
            },
            initialDataUpdatedAt: mountedAt,
        }),
    })

    const isFollowing = followStatus?.isFollowing ?? false

    const toggleFollow = useMutation({
        mutationFn: async ({
            wasFollowing,
            followRef,
        }: {
            wasFollowing: boolean
            followRef?: string
        }): Promise<FollowStatus> => {
            if (!identifier || !adapterSession || !adapter) throw new Error('Not authenticated')
            if (wasFollowing) {
                await adapter.unfollow(adapterSession, identifier, followRef)
                return { isFollowing: false, followRef: undefined }
            } else {
                return adapter.follow(adapterSession, identifier)
            }
        },
        onMutate: ({ wasFollowing }) => {
            // Fire-and-forget: abort signal is sent synchronously even without await.
            // Dropping the await keeps this handler synchronous so React 18 batches
            // the isPending=true and isFollowing=<optimistic> updates into one render,
            // preventing the button from briefly disappearing between state changes.
            void queryClient.cancelQueries({ queryKey })
            const previous = queryClient.getQueryData<FollowStatus>(queryKey)
            queryClient.setQueryData<FollowStatus>(queryKey, {
                isFollowing: !wasFollowing,
                followRef: previous?.followRef,
            })
            return { previous }
        },
        onSuccess: (result, { wasFollowing, followRef }) => {
            queryClient.setQueryData<FollowStatus>(queryKey, result)

            const delta = wasFollowing ? -1 : 1

            if (platform === 'bluesky') {
                if (wasFollowing && identifier) {
                    // Unfollowed: remove from cached following lists
                    removeFromFollowingLists(queryClient, identifier)
                } else {
                    // Followed: clear so it refetches with the new entry
                    queryClient.removeQueries({ queryKey: ['following'] })
                }
                queryClient.removeQueries({ queryKey: ['followers'] })
                queryClient.removeQueries({ queryKey: ['ap-followers'] })
                queryClient.removeQueries({ queryKey: ['ap-following'] })

                // Adjust the authenticated user's own following count
                queryClient.setQueriesData<ProfileData | null>(
                    { queryKey: ['profile', 'bluesky'] },
                    old => {
                        if (!old || old.followsCount == null) return old
                        return { ...old, followsCount: Math.max(0, old.followsCount + delta) }
                    }
                )
            } else {
                // AP protocols (mastodon, pixelfed)
                // followRef is the accountId for AP protocols
                // Use pre-mutation followRef from variables (result.followRef is undefined after unfollow)
                const accountId = followRef ?? result.followRef
                if (wasFollowing && accountId) {
                    removeFromAPFollowingLists(queryClient, accountId)
                } else {
                    queryClient.removeQueries({ queryKey: ['ap-following'] })
                }
                queryClient.removeQueries({ queryKey: ['followers'] })
                queryClient.removeQueries({ queryKey: ['ap-followers'] })
                queryClient.removeQueries({ queryKey: ['following'] })

                // Followed-hashtag attribution reads follow state from its own
                // account-scoped relationship cache. Invalidate it broadly so a
                // follow/unfollow immediately refreshes attribution on the
                // Following timeline rather than waiting out the staleTime.
                void queryClient.invalidateQueries({
                    queryKey: ['mastodonHashtagAttribution', 'relationship'],
                })

                // Adjust the authenticated user's own following count
                queryClient.setQueriesData<ProfileData | null>(
                    { queryKey: ['profile', platform] },
                    old => {
                        if (!old || old.followsCount == null) return old
                        return { ...old, followsCount: Math.max(0, old.followsCount + delta) }
                    }
                )
            }

            // Adjust the viewed profile's followers count
            queryClient.setQueriesData<ProfileWithBridgeStatus>(
                { queryKey: ['viewProfile', 'graphql', identifier] },
                old => {
                    if (!old || old.followersCount == null) return old
                    return { ...old, followersCount: Math.max(0, old.followersCount + delta) }
                }
            )
        },
        onError: (_err, _vars, context) => {
            if (context?.previous !== undefined) {
                queryClient.setQueryData<FollowStatus>(queryKey, context.previous)
            }
        },
    })

    return {
        isFollowing,
        /** True while the initial follow status is fetching OR while a follow/unfollow
         *  mutation is in-flight. Use this to disable interactive controls. */
        isLoading: isStatusLoading || toggleFollow.isPending,
        /** True only while the initial follow status is fetching (before we know the
         *  current follow state). Use this to hide UI that requires knowing the state
         *  (e.g. returning null), rather than using `isLoading`, which would also
         *  hide the button during a pending mutation. */
        isStatusLoading,
        handleToggleFollow: (options?: { onSuccess?: () => void; onError?: () => void }) => {
            const current = queryClient.getQueryData<FollowStatus>(queryKey)
            if (!current) return // Status not loaded yet
            toggleFollow.mutate(
                { wasFollowing: current.isFollowing, followRef: current.followRef },
                { onSuccess: options?.onSuccess, onError: options?.onError }
            )
        },
    }
}
